import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, Legend } from "recharts";
import { BarChart3, Truck, Users, Activity, TrendingUp, ThumbsUp, X, Loader2, MapPin, Package } from "lucide-react";
import api from "@/lib/api";
import { useAssetConfig } from "@/hooks/useAssetConfig";

const CHART_COLORS = ["#84cc16", "#3b82f6", "#f59e0b", "#a855f7", "#ef4444", "#06b6d4", "#f97316"];

export default function AnalyticsDashboard({ hubId, session, onClose }) {
  const ac = useAssetConfig(session);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getAnalytics(hubId).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [hubId]);

  if (loading) return (
    <div className="fixed inset-0 z-[900] bg-white/95 flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-lime-500 animate-spin" />
    </div>
  );
  if (!data) return null;

  const totalItemA = data.driver_fuel?.reduce((s, d) => s + d.diesel, 0) || 0;
  const totalDeliveries = data.driver_fuel?.reduce((s, d) => s + d.deliveries, 0) || 0;
  const totalRequests = data.requests_by_type?.reduce((s, r) => s + r.count, 0) || 0;

  const itemALabel = ac.itemA;
  const itemAUnit = ac.itemAUnit;
  const itemBLabel = ac.itemB;
  const itemBUnit = ac.itemBUnit;
  const isTaskBased = ac.isTaskBased;

  return (
    <div className="fixed inset-0 z-[900] bg-white/95 overflow-y-auto" data-testid="analytics-dashboard">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8 sticky top-0 bg-white/95 backdrop-blur-sm py-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-slate-950" />
            </div>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight">Hub Analytics</h1>
              <p className="text-xs text-slate-400 font-bold">Real-time insights from your operations</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2.5 bg-slate-100 rounded-xl text-slate-400 hover:text-slate-900" data-testid="close-analytics">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: isTaskBased ? "Total Tasks" : `Total ${itemALabel}`, value: isTaskBased ? totalDeliveries : `${totalItemA.toLocaleString()}${itemAUnit}`, icon: isTaskBased ? Activity : Package, color: "lime" },
            { label: ac.deliveryLabel + "s", value: totalDeliveries, icon: Truck, color: "blue" },
            { label: `Total ${ac.requestLabel}s`, value: totalRequests, icon: Activity, color: "amber" },
            { label: "Feedback Score", value: `${data.feedback?.positive || 0}/${(data.feedback?.positive || 0) + (data.feedback?.negative || 0)}`, icon: ThumbsUp, color: "purple" },
          ].map((kpi, i) => (
            <div key={i} className="p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid={`kpi-card-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <kpi.icon className={`w-4 h-4 text-${kpi.color}-500`} />
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{kpi.label}</span>
              </div>
              <p className="text-2xl font-black text-slate-900 font-mono">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Driver/Operator Consumption */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="chart-driver-fuel">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-lime-500" /> {ac.hasDriver ? "Driver" : "Operator"} {isTaskBased ? "Task Completion" : `${itemALabel} Usage`}
            </h3>
            {data.driver_fuel?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.driver_fuel} barSize={24}>
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                  <Bar dataKey="diesel" fill="#84cc16" name={`${itemALabel} (${itemAUnit})`} radius={[4, 4, 0, 0]} />
                  {ac.itemBEnabled && <Bar dataKey="adblue" fill="#3b82f6" name={`${itemBLabel} (${itemBUnit})`} radius={[4, 4, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-12">No {ac.deliveryLabel.toLowerCase()} data yet</p>}
          </div>

          {/* Requests by Type */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="chart-requests-type">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-amber-500" /> {ac.requestLabel}s by Type
            </h3>
            {data.requests_by_type?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={data.requests_by_type} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {data.requests_by_type.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-12">No {ac.requestLabel.toLowerCase()}s yet</p>}
          </div>

          {/* Daily Activity */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="chart-daily-activity">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-500" /> Activity (30 days)
            </h3>
            {data.daily_activity?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={data.daily_activity}>
                  <defs>
                    <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#84cc16" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#84cc16" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 8 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                  <Area type="monotone" dataKey="events" stroke="#84cc16" fill="url(#actGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-12">No activity data</p>}
          </div>

          {/* Site Subject Distribution */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="chart-site-machines">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-500" /> {isTaskBased ? "Tasks per Site" : "Subjects per Site"}
            </h3>
            {data.site_machines?.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.site_machines} layout="vertical" barSize={16}>
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 9 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} width={100} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                  <Bar dataKey="machines" fill="#a855f7" name={isTaskBased ? "Tasks" : "Subjects"} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-xs text-slate-400 text-center py-12">No sites yet</p>}
          </div>

          {/* Feedback Breakdown */}
          {(data.feedback?.positive > 0 || data.feedback?.negative > 0) && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 md:col-span-2" data-testid="chart-feedback">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                <ThumbsUp className="w-4 h-4 text-lime-500" /> Feedback & Incidents
              </h3>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={[{ name: "Positive", value: data.feedback.positive }, { name: "Negative", value: data.feedback.negative }]} dataKey="value" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                        <Cell fill="#84cc16" />
                        <Cell fill="#ef4444" />
                      </Pie>
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {data.feedback.incidents?.length > 0 && (
                  <div>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={data.feedback.incidents} barSize={20}>
                        <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 8 }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fill: "#64748b", fontSize: 9 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, fontSize: 11 }} />
                        <Bar dataKey="count" fill="#ef4444" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
